/**
 * 영업진행 API 클라이언트 — backend /sales/* 와 1:1.
 */

import { apiErrorText } from './api-error-text';
import { todayKst } from './date-label';
import type { ApiResponse, ApiResponseList } from './types';
import type {
  AuditEntry,
  SalesDetail,
  SalesListRow,
  SalesMemoEntry,
  SalesSplitInfo,
  SalesSplitRequest,
  SalesStatus,
  SalesStage,
} from './types';

const BASE = '/api';

export interface SalesListParams {
  page?: number;
  pageSize?: number;
  q?: string;
  stage?: SalesStatus[];
  /** 연도 다중 (invoice_date YEAR, NULL 이면 sales_id 앞 4자리 fallback) — /sales/years 와 동일 식 */
  year?: number[];
  /** 사업자 단일 (SM/EJ). 미전달=전체. */
  companyId?: 'SM' | 'EJ';
  /**
   * 제안업체 **정확 일치** (v2.79.9) — 견적서를 기존 영업건에 연동할 때
   * 「그 견적서 수신 업체의 영업건」만 추리는 데 쓴다. `q` 는 사업명·엔드유저까지
   * 훑어 엉뚱한 건이 섞이므로 이 칸을 따로 쓴다.
   */
  proposalCompany?: string;
  /** 📄 판보 미작성만 (v2.305.0 · #20) — 상태 발행 중 판보(자체·묶음·과거 엑셀)가 없는 건. 켜면 서버가 stage 를 보지 않는다 */
  reportMissing?: boolean;
}

export async function listSales(params: SalesListParams = {}): Promise<{
  data: SalesListRow[];
  page: { page: number; pageSize: number; total: number };
}> {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  if (params.q) qs.set('q', params.q);
  if (params.stage && params.stage.length > 0) {
    for (const s of params.stage) qs.append('stage', s);
  }
  if (params.year && params.year.length > 0) {
    for (const y of params.year) qs.append('year', String(y));
  }
  if (params.companyId) qs.set('companyId', params.companyId);
  if (params.proposalCompany) qs.set('proposalCompany', params.proposalCompany);
  if (params.reportMissing) qs.set('reportMissing', '1');
  const res = await fetch(`${BASE}/sales?${qs.toString()}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponseList<SalesListRow>;
  if (!json.ok) {
    throw new Error(json.error.message);
  }
  return { data: json.data, page: json.page };
}

/** 영업진행 등록 연도 목록 (sales_id 앞 4자리 distinct, 최신 desc) — 연도 드롭다운용 */
export async function listSalesYears(): Promise<string[]> {
  const res = await fetch(`${BASE}/sales/years`, { credentials: 'include' });
  const json = (await res.json()) as { ok: boolean; years: string[]; error?: { message: string } };
  if (!json.ok) {
    throw new Error(json.error?.message ?? '연도 목록 조회 실패');
  }
  return json.years;
}

export async function getSalesDetail(salesId: string): Promise<SalesDetail> {
  const res = await fetch(`${BASE}/sales/${encodeURIComponent(salesId)}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<SalesDetail>;
  if (!json.ok) {
    throw new Error(json.error.message);
  }
  return json.data;
}

/** 납품확인증 PDF URL — 견적서가 등록된 영업건만 생성 가능(서버에서 가드).
 *  openServerPdfPreview 로 팝업 미리보기(다운로드 겸용). */
export function salesDeliveryConfirmationUrl(salesId: string): string {
  return `${BASE}/sales/${encodeURIComponent(salesId)}/delivery-confirmation.pdf`;
}

/** 과거 판매보고서 — 첫 탭 PDF 미리보기 URL (inline). openServerPdfPreview 로 팝업. */
export function salesLegacyReportPreviewUrl(salesId: string): string {
  return `${BASE}/sales/${encodeURIComponent(salesId)}/legacy-report/preview`;
}

/** 과거 판매보고서 — 원본 엑셀 다운로드 URL. */
export function salesLegacyReportDownloadUrl(salesId: string): string {
  return `${BASE}/sales/${encodeURIComponent(salesId)}/legacy-report/download`;
}

/* ── 검수명판 ───────────────────────────────────────── */

export interface NameplateItem {
  businessName: string; // 사업명
  itemName: string;     // 품명
  spec: string;         // 규격
  qty: number;          // 수량
}

export interface NameplateItemsResult {
  projectName: string;
  /** 영업건에 첨부된 분납서 개수 (0 = 직접 작성 모드) */
  bunapCount: number;
  items: (NameplateItem & { source: string })[];
  warnings: string[];
}

/** 검수명판 항목 자동 추출 — 첨부된 분납서(분할납품요구서)를 파싱해 사업명·품명·규격·수량을 뽑는다 */
export async function fetchNameplateItems(salesId: string): Promise<NameplateItemsResult> {
  const res = await fetch(`${BASE}/sales/${encodeURIComponent(salesId)}/nameplate-items`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<NameplateItemsResult>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 검수명판 PDF 생성 (항목당 1페이지, A4 가로) — 반환 { blob, filename } */
export async function generateNameplatePdf(
  salesId: string,
  items: NameplateItem[],
): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`${BASE}/sales/${encodeURIComponent(salesId)}/nameplate.pdf?inline=1`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) {
    const j = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(j?.error?.message ?? `검수명판 PDF 생성 실패 (${res.status})`);
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') ?? '';
  const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
  const filename = utf8Match
    ? decodeURIComponent(utf8Match[1]!)
    : `검수명판_${salesId}.pdf`;
  return { blob, filename };
}

/** 판매보고서 PDF 다운로드 (저장 안 함, 임시 확인용) — 반환 Blob.
 *  memberSalesIds: 묶음(합본) 판보 미리보기용 — 화면의 현재 묶음 선택 그대로 반영 (v1.6.0) */
export async function downloadReportPdf(
  salesId: string,
  reportNote: string,
  memberSalesIds?: string[],
): Promise<Blob> {
  const res = await fetch(`${BASE}/sales/${encodeURIComponent(salesId)}/report-pdf/download`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reportNote, memberSalesIds }),
  });
  if (!res.ok) {
    const j = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(j?.error?.message ?? `PDF 다운로드 실패 (${res.status})`);
  }
  return await res.blob();
}

/** 판매보고서 PDF 영업건에 저장 (entity_attachment + report_note UPDATE + 결재 등재).
 *  memberSalesIds: 묶음(합본) 판보 — 함께 묶을 영업건 목록으로 교체, []=해제 (v1.6.0) */
export async function saveReportPdf(
  salesId: string,
  reportNote: string,
  memberSalesIds?: string[],
): Promise<{ attachmentId: number; filename: string; status?: string; memberSalesIds?: string[] }> {
  const res = await fetch(`${BASE}/sales/${encodeURIComponent(salesId)}/report-pdf/save`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reportNote, memberSalesIds }),
  });
  const json = (await res.json()) as ApiResponse<{
    attachmentId: number;
    filename: string;
    approvalStatus?: string;
    memberSalesIds?: string[];
  }>;
  if (!json.ok) throw new Error(json.error.message);
  return { ...json.data, status: json.data.approvalStatus };
}

/** 묶음(합본) 판보 후보 조회 — 같은 담당·발행 후·전량 출고·판보 미작성 건 (v1.6.0) */
export async function listReportBundleCandidates(
  salesId: string,
): Promise<import('./types').ReportBundleCandidate[]> {
  const res = await fetch(
    `${BASE}/sales/${encodeURIComponent(salesId)}/report-bundle-candidates`,
    { credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<{
    candidates: import('./types').ReportBundleCandidate[];
  }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.candidates;
}

export async function getSalesAudit(salesId: string): Promise<AuditEntry[]> {
  const res = await fetch(`${BASE}/sales/${encodeURIComponent(salesId)}/audit`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<AuditEntry[]>;
  if (!json.ok) {
    throw new Error(json.error.message);
  }
  return json.data;
}

// ─── 등록/수정/삭제 (Phase 6-3) ──────────────────────────

export async function peekNextSalesId(): Promise<string> {
  const res = await fetch(`${BASE}/sales/next-id`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<{ nextSalesId: string }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.nextSalesId;
}

export interface SalesCreateInput {
  empId: string;
  companyId: 'SM' | 'EJ';
  /** 옛 통로 — 요약값 하나. 새 화면은 아래 셋을 보낸다 (v2.103.0) */
  status?: SalesStatus;
  /** 순차 단계 — 일반/유력/발주/납품 (v2.103.0) */
  stage?: SalesStage;
  /** 발행 켬/끔 (v2.103.0) */
  invoiceIssued?: boolean;
  /** 판보 켬/끔 — 수정 때 「끄는」 경우에만 보낸다 (v2.103.0) */
  reportDone?: boolean;
  invoiceCompany?: string | null;
  proposalCompany?: string | null;
  endUser?: string | null;
  deliveryPlace?: string | null;
  projectName?: string | null;
  contactPerson?: string | null;
  itemSummary?: string | null;
  /** 제안가 — **viewer 에게는 null** (v2.79.7 서버 마스킹). 화면은 없으면 `***` 로 표시할 것 */
  proposalPrice: number | null;
  contractDate?: string | null;
  deliveryDate?: string | null;
  expectedDeliveryDate?: string | null;
  /** 납품예정 시간(선택, HH:MM) — 비우면 웍스 캘린더에 종일 일정 (2026-07-29) */
  deliveryStartTime?: string | null;
  deliveryEndTime?: string | null;
  invoiceDate?: string | null;
  note?: string | null;
  reportStatus?: string | null;
  costNote?: string | null;
  /** 다음 할 일(v2.319.0) — 글이 바뀌면 서버가 next_step_at 을 지금으로 */
  nextStep?: string | null;
  nextStepDate?: string | null;
  isTrackRecord?: boolean;
  parentSalesId?: string | null;
}

/**
 * 영업진행 엑셀 다운로드 — 운영 서식 템플릿 + 화면의 현재 행 (필터·정렬 적용된).
 * 사용자가 [📐 내 표 설정] 으로 숨긴 컬럼은 무시되고, 템플릿의 컬럼 구조 그대로 출력.
 */
export async function downloadSalesExcel(rows: SalesListRow[]): Promise<void> {
  const res = await fetch(`${BASE}/sales/export`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows }),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`엑셀 다운로드 실패 (${res.status}): ${txt}`);
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') ?? '';
  const match = /filename\*=UTF-8''([^;]+)/.exec(cd);
  const filename = match ? decodeURIComponent(match[1]) : `영업진행_${todayKst()}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function createSales(input: SalesCreateInput): Promise<string> {
  const res = await fetch(`${BASE}/sales`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiResponse<{ salesId: string }>;
  if (!json.ok) throw new Error(apiErrorText(json.error));   // 어느 칸이 왜 틀렸는지까지 (v2.228.1)
  return json.data.salesId;
}

export type SalesUpdateInput = Partial<Omit<SalesCreateInput, 'parentSalesId'>>;

export interface StockNegativeWarning {
  itemCode: string;
  itemName: string | null;
  currentStock: number;
}

export interface SalesUpdateResult {
  salesId: string;
  warnings: StockNegativeWarning[];
  /**
   * 적용 여부:
   *   - true  : 변경 적용됨 (warnings 가 있어도 force=true 또는 첫 호출에서 경고 없음)
   *   - false : 음수 경고 있어 사용자 확인 필요. 트랜잭션 ROLLBACK 됨. force=true 로 재호출 필요.
   */
  applied: boolean;
}

/**
 * 음수 재고 경고 발생 시:
 *   1차 호출 (force 미전달) → 백엔드 ROLLBACK → applied=false + warnings
 *   사용자 확인 후 2차 호출 (force=true) → 실제 적용
 */
export async function updateSales(
  salesId: string,
  input: SalesUpdateInput,
  options: { force?: boolean } = {},
): Promise<SalesUpdateResult> {
  const body = options.force ? { ...input, force: true } : input;
  const res = await fetch(`${BASE}/sales/${encodeURIComponent(salesId)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as ApiResponse<SalesUpdateResult>;
  if (!json.ok) throw new Error(apiErrorText(json.error));   // 어느 칸이 왜 틀렸는지까지 (v2.228.1)
  return {
    salesId: json.data.salesId,
    warnings: json.data.warnings ?? [],
    applied: json.data.applied ?? true,
  };
}

export async function deleteSales(salesId: string): Promise<void> {
  const res = await fetch(`${BASE}/sales/${encodeURIComponent(salesId)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<{ salesId: string }>;
  if (!json.ok) throw new Error(json.error.message);
}

// ─── 반품 처리 (2026-07-03 신설 · 2026-07-16 개편) ──────────
//
// 계산서 발행 전(status='납품' + 발행일 없음) 영업건의 전량 반품.
// 재고 자동 복귀 + 상태 '일반' 복귀(비고에 이력 기록). mgmt/admin 전용.

export interface SalesReturnResult {
  salesId: string;
  status: string;
  returnDate: string;
  releaseItemsReturned: number;
  restoredPurchases: number;
  /** 발행요청이 이미 발송돼 있던 건 — 경영지원팀에 발행 취소 안내 필요 */
  hadPendingInvoiceRequest: boolean;
}

export async function returnSales(
  salesId: string,
  input: { returnDate: string; reason?: string },
): Promise<SalesReturnResult> {
  const res = await fetch(`${BASE}/sales/${encodeURIComponent(salesId)}/return`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiResponse<SalesReturnResult>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

// ─── 분할(계산서 기준 · 선금/잔금 등) (v2.221.0) ─────────────
//   원래 건 = 1번 조각(번호 그대로), 새 조각 = 번호-01·-02 … — 규칙은 backend lib/sales/split.ts 머리말.

export async function getSplitInfo(salesId: string): Promise<SalesSplitInfo> {
  const res = await fetch(`${BASE}/sales/${encodeURIComponent(salesId)}/split-info`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<SalesSplitInfo>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export interface SalesSplitResult {
  logId: number;
  partIds: string[];
  movedAttachments: number;
  movedPurchases: number;
  movedMisc: number;
}

export async function splitSales(salesId: string, input: SalesSplitRequest): Promise<SalesSplitResult> {
  const res = await fetch(`${BASE}/sales/${encodeURIComponent(salesId)}/split`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiResponse<SalesSplitResult>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 분할 취소(합치기) — salesId = 그 분할의 원래 건 */
export async function undoSplit(salesId: string, logId: number): Promise<{ sourceId: string; removedIds: string[]; amount: number }> {
  const res = await fetch(`${BASE}/sales/${encodeURIComponent(salesId)}/split-undo`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ logId }),
  });
  const json = (await res.json()) as ApiResponse<{ sourceId: string; removedIds: string[]; amount: number }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

// ─── 매입 연결 / 해제 (2026-05-17) ─────────────────────────
//
// link: 영업 [+ 매입 연결] 모달에서 호출. 기존 매입관리의 재고보유 매입을
//       item_code + qty 단위로 영업에 연결. backend 가 FIFO 분할 처리.
// unlink: 영업 수정 화면의 매입 내역에서 🗑 클릭 시 호출. sales_id NULL + 출고 흔적 정리.

export interface LinkPurchaseResult {
  salesId: string;
  itemCode: string;
  qty: number;
  affectedPurchaseIds: number[];
}

export async function linkPurchaseToSales(
  salesId: string,
  input: { itemCode: string; qty: number; spec?: string | null },
  force = false,
): Promise<LinkPurchaseResult> {
  const res = await fetch(
    `${BASE}/sales/${encodeURIComponent(salesId)}/link-purchase`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(force ? { ...input, force: true } : input),
    },
  );
  const json = (await res.json()) as ApiResponse<LinkPurchaseResult>;
  if (!json.ok) {
    // 과거 판보 원가 소급분과 겹침 → 사람에게 묻고 [확인] 하면 force 로 재시도 (v2.169.0).
    const reason = (json.error.details as { reason?: string } | undefined)?.reason;
    if (!force && json.error.code === 'CONFLICT' && reason === 'LEGACY_COST_BACKFILLED') {
      if (window.confirm(json.error.message)) {
        return linkPurchaseToSales(salesId, input, true);
      }
      throw new Error('취소되었습니다');
    }
    throw new Error(json.error.message);
  }
  return json.data;
}

export async function unlinkPurchaseFromSales(
  salesId: string,
  purchaseId: number,
): Promise<void> {
  const res = await fetch(
    `${BASE}/sales/${encodeURIComponent(salesId)}/unlink-purchase/${purchaseId}`,
    { method: 'POST', credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<{ salesId: string; purchaseId: number }>;
  if (!json.ok) throw new Error(json.error.message);
}

// ─── 📅 납품예정일만 넣기 (v2.283.0 · 개발요청 #4) ─────────────────────
//
// 조회 전용(기술지원팀) 포함 전 직원 · 납품 전(일반·유력·발주)이고 판보 전인 건만(발행은 상관없음 · v2.304.0 #15) · 예정일·시작·종료 세 칸만.
// 저장하면 서버가 캘린더 「[납품예정] 엔드유저 품목」 일정을 바로 맞춘다(같은 날·같은 엔드유저는 한 일정).

/** 예정일을 넣을 수 있는 진행단계 — 서버 EXPECTED_DELIVERY_EDITABLE_STAGES 와 같다 */
export const EXPECTED_DELIVERY_EDITABLE_STAGES = ['일반', '유력', '발주'] as const;

export async function setExpectedDelivery(
  salesId: string,
  p: { expectedDeliveryDate: string; deliveryStartTime: string | null; deliveryEndTime: string | null },
): Promise<{ calendarMessage?: string }> {
  const res = await fetch(`${BASE}/sales/${encodeURIComponent(salesId)}/expected-delivery`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(p),
  });
  const json = (await res.json()) as ApiResponse<{ calendarMessage?: string }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

// ─── 메모 한 건 더하기·지우기 (v2.204.0) ─────────────────────
//
// 상세 화면에서 **바로** 저장한다. 서버가 행을 잠그고 한 건만 더하거나 빼므로(목록 통째 덮어쓰기 아님)
// 두 사람이 비슷한 때 달아도 한쪽이 사라지지 않는다. 돌아오는 것은 저장된 뒤의 메모 목록(최신이 맨 앞).

export async function addSalesMemo(salesId: string, content: string): Promise<SalesMemoEntry[]> {
  const res = await fetch(`${BASE}/sales/${encodeURIComponent(salesId)}/memos`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content }),
  });
  const json = (await res.json()) as ApiResponse<{ memos: SalesMemoEntry[] }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.memos;
}

export async function deleteSalesMemo(
  salesId: string,
  memoId: string,
): Promise<{ memos: SalesMemoEntry[]; removed: boolean }> {
  const res = await fetch(
    `${BASE}/sales/${encodeURIComponent(salesId)}/memos/${encodeURIComponent(memoId)}`,
    { method: 'DELETE', credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<{ memos: SalesMemoEntry[]; removed: boolean }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

// ─── 기타비용 (sales_misc_cost, v1.5.0) ─────────────────────
//
// 숙박비·운송비 등 자유입력 직접경비 — 매입관리 밖, 영업건 원가에만 합산.

export interface MiscCostInput {
  label: string;
  amount: number;
  costDate?: string | null;
  note?: string | null;
}

export async function addMiscCost(salesId: string, input: MiscCostInput): Promise<number> {
  const res = await fetch(`${BASE}/sales/${encodeURIComponent(salesId)}/misc-costs`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.id;
}

export async function updateMiscCost(
  salesId: string,
  id: number,
  input: Partial<MiscCostInput>,
): Promise<void> {
  const res = await fetch(
    `${BASE}/sales/${encodeURIComponent(salesId)}/misc-costs/${id}`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  );
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
}

export async function deleteMiscCost(salesId: string, id: number): Promise<void> {
  const res = await fetch(
    `${BASE}/sales/${encodeURIComponent(salesId)}/misc-costs/${id}`,
    { method: 'DELETE', credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<{ id: number }>;
  if (!json.ok) throw new Error(json.error.message);
}

// ─── 세금계산서 발행요청 양식 (경영지원팀 전달) ─────────────────
//
// 영업진행에서 건 선택 → prepare 로 7개 항목 자동완성 → 사용자 수정 → send 로 네이버웍스 전송.

/** 발행요청 품목 한 줄 — [+ 품목 추가]로 한 계산서에 여러 개 (2026-07-28). */
export interface TaxInvoiceRequestItem {
  itemName: string;
  qty: number | null;
  unitPrice: number | null;
}

/** 발행요청 편집행 — prepare 자동완성 후 사용자가 수정 가능. */
export interface TaxInvoiceRequestRow {
  salesId: string;
  issueDate: string;       // 발행일자 (기본=계산서발행일 또는 오늘)
  supplier: string;        // 공급자 (자사 사업자명)
  supplierBizNo: string;   // 공급자 사업자번호 (표시용)
  companyId: string | null;
  customer: string;        // 고객사 (계산서발행업체)
  itemName: string;        // 품목명 (제안품목 요약) — 화면은 items 를 편집, 이 칸은 호환용
  amount: number;          // 금액 (공급가액)
  /** 영업건에 원래 있던 금액(제안가) — 요청 금액이 이것과 다르면 전송 차단 (2026-07-27) */
  originalAmount: number;
  /** 수량·단가 = 선택 입력. 둘 다 입력하면 금액 = 수량 × 단가 (자동 계산) — items 호환용 단일 칸 */
  unitPrice: number | null;
  qty: number | null;
  /** 품목 목록 — 화면 편집의 원천. 2개 이상이면 품목마다 수량·단가 필수, 금액 = 합계 자동 */
  items: TaxInvoiceRequestItem[];
  email1: string;          // 세금계산서 이메일 1
  email2: string;          // 세금계산서 이메일 2
  email1WasEmpty: boolean; // 거래처에 이메일1 칸이 비어있었나 (백세이브 판정)
  email2WasEmpty: boolean;
  partnerId: number | null; // 매칭된 거래처 id (백세이브 대상)
  paymentDueDate: string;   // 결제예정일 (직접 입력)
  /** 비고 = 발행될 세금계산서의 '비고'란에 기재할 내용 (선택, 200자) — 2026-07-29, v2.019 */
  remark: string;
  alreadyRequestedAt: string | null; // 이미 발행요청 보낸 시각 (있으면 중복 경고)
}

export async function prepareTaxInvoiceRequest(salesIds: string[]): Promise<TaxInvoiceRequestRow[]> {
  const res = await fetch(`${BASE}/sales/tax-invoice-request/prepare`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ salesIds }),
  });
  const json = (await res.json()) as ApiResponse<{ items: TaxInvoiceRequestRow[] }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.items;
}

export interface TaxInvoiceSendResult {
  sent: boolean;
  reason?: string;
  count?: number;
  savedEmails?: number;
  /** 영업정보 표 이미지 동봉 전송 여부 (텍스트는 보냈지만 이미지만 실패할 수 있음) */
  imageSent?: boolean;
  /** 영업정보 엑셀(.xlsx) 첨부 전송 여부 */
  fileSent?: boolean;
}

/** 최종 확인용 미리보기 결과 (v2.044) — 전송은 일어나지 않는다. */
export interface TaxInvoicePreviewResult {
  /** 보낼 수 있는 상태인지 (서버 검증 통과) */
  canSend: boolean;
  /** 네이버웍스에 실제로 나갈 문안 원문 — canSend 일 때만 */
  text?: string;
  count?: number;
  /** 보낼 수 없는 사유 — canSend 가 false 일 때 */
  reason?: string;
  /**
   * 보낼 수는 있지만 알려야 할 것 (v2.76.0) — 이미 계산서가 발행된 영업건에 재요청하는 경우.
   * 차단이 아니라 확인용. 수정발행은 정상 업무라 막지 않는다.
   */
  warning?: string | null;
}

/**
 * 발행요청 최종 확인 — 전송 없이 '나갈 문안'만 받아온다 (v2.044).
 * 서버는 /send 와 **같은 함수**를 dryRun 으로 태우므로 확인창 내용 = 실제 전송분.
 */
export async function previewTaxInvoiceRequest(
  rows: TaxInvoiceRequestRow[],
): Promise<TaxInvoicePreviewResult> {
  const res = await fetch(`${BASE}/sales/tax-invoice-request/preview`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows }),
  });
  const json = (await res.json()) as ApiResponse<{
    canSend?: boolean; text?: string; count?: number; reason?: string; warning?: string | null;
  }>;
  if (!json.ok) throw new Error(json.error.message);
  return {
    canSend: json.data.canSend === true,
    text: json.data.text,
    count: json.data.count,
    reason: json.data.reason,
    warning: json.data.warning ?? null,
  };
}

export async function sendTaxInvoiceRequest(rows: TaxInvoiceRequestRow[]): Promise<TaxInvoiceSendResult> {
  const res = await fetch(`${BASE}/sales/tax-invoice-request/send`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows }),
  });
  const json = (await res.json()) as ApiResponse<TaxInvoiceSendResult>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export interface TaxInvoiceWithdrawResult {
  sent: boolean;
  reason?: string;
  count?: number;
}

/** 발행요청 철회 — 경영지원팀 방에 '철회 안내' 후속 메시지 전송 + 🧾 표식 해제. */
export async function withdrawTaxInvoiceRequest(salesIds: string[]): Promise<TaxInvoiceWithdrawResult> {
  const res = await fetch(`${BASE}/sales/tax-invoice-request/withdraw`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ salesIds }),
  });
  const json = (await res.json()) as ApiResponse<TaxInvoiceWithdrawResult>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

// ── 영업진행 [세금계산서] 탭(v2.290.0 전엔 거래 문서함) — 발행요청 이력 + 수신·처리 내역 ────────

export interface TaxInvoiceRequestHistoryRow {
  id: number;
  salesId: string;
  requestedAt: string;          // 요청 일시
  requesterName: string;        // 요청자 (직원명, 없으면 로그인 ID)
  customer: string | null;      // 요청 양식의 고객사(계산서 발행업체)
  itemName: string | null;      // 요청 양식의 품목명
  amount: number;               // 공급가액
  unitPrice: number | null;     // 요청 양식의 단가 (선택 입력 — null=미입력)
  qty: number | null;           // 요청 양식의 수량 (선택 입력 — null=미입력)
  /** 다품목 요청의 품목 명세 (단일 품목 요청은 null) — 2026-07-28 */
  items: TaxInvoiceRequestItem[] | null;
  issueDate: string | null;     // 요청 양식의 발행일자
  withdrawnAt: string | null;   // 철회 시각 (null=철회 안 됨)
  /** 세금계산서 비고란에 기재할 내용 (선택 입력 — null=미입력) — 2026-07-29, v2.019 */
  remark: string | null;
  salesStatus: string | null;   // 영업건 현재 진행단계
  invoiceDate: string | null;   // 영업건 계산서 발행일 (옛 요청의 발행완료 판정용)
  projectName: string | null;
  taxInvoiceCount: number;      // 세금계산서 PDF 첨부 수 (📑 게이팅)
  /** 이 요청이 계산서로 채워진 시각 (v2.76.0) — null=아직 요청중 */
  fulfilledAt: string | null;
  /** 이 요청을 채운 계산서 승인번호 (v2.76.0) */
  fulfilledApprovalNo: string | null;
  /** 요청 시점에 이미 계산서가 있었음 = 수정발행 요청 (v2.76.0) */
  isRevision: boolean;
  /** v2.76.0 이전 요청 — fulfilledAt 기록이 없어 옛 방식(invoiceDate)으로 판정해야 함 */
  legacy: boolean;
}

export async function listTaxInvoiceRequestHistory(): Promise<TaxInvoiceRequestHistoryRow[]> {
  const res = await fetch(`${BASE}/sales/tax-invoice-request/history`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<{ items: TaxInvoiceRequestHistoryRow[] }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.items;
}

export interface TaxInvoiceReceiptRow {
  id: number;
  approvalNo: string;           // 국세청 승인번호
  salesId: string | null;       // 자동첨부된 영업건 (실패 시 null)
  matchStatus: string;          // 'auto'(봇 자동) | 'manual'(사람이 지정) | 'failed' | 'dismissed'(표시에서 뺌 · v2.311.0)
  failReason: string | null;
  buyerName: string | null;     // 공급받는자 상호
  buyerBizNo: string | null;
  issueDate: string | null;     // 작성일자
  supplyAmount: number | null;  // 공급가액
  totalAmount: number | null;
  filename: string | null;
  attachmentId: number | null;  // 첨부 성공분의 entity_attachment.id (PDF 보기, 삭제됐으면 null)
  receivedAt: string;
  projectName: string | null;
  /** 수동 지정한 사람 (matchStatus='manual' 일 때) — v2.47.0 */
  manualByName: string | null;
  manualAt: string | null;
  /** 실패 건에 보관된 PDF 가 있는지 — 있어야 [영업건 지정] 가능 (v2.47.0) */
  keptPdf: boolean;
  /** 🔧 유지보수 계약의 한 달분에 붙은 계산서 (v2.262.0) — 영업건 대신 */
  maintenance: {
    maintenanceId: number;
    contractId: number | null;
    endUser: string | null;
    year: number | null;
    month: number | null;
  } | null;
}

/** 미매칭 표시 (v2.311.0 · 개발요청 #25) — 봇이 못 붙인 「실패」 계산서 수 · 영업진행 [🧾 세금계산서] 탭·[수신·처리 내역]에 빨갛게 */
export async function getTaxInvoiceUnmatchedCount(): Promise<number> {
  const res = await fetch(`${BASE}/sales/tax-invoice-receipts/unmatched-count`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<{ failed: number }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.failed;
}

/** 표시에서 빼기 (v2.311.0) — 포털에 붙일 곳이 없는 「실패」 계산서 · 기록·PDF 는 그대로(나중에 [🔗 영업건 지정] 가능) */
export async function dismissTaxInvoiceReceipt(receiptId: number, reason: string): Promise<void> {
  const res = await fetch(`${BASE}/sales/tax-invoice-receipts/${receiptId}/dismiss`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  const json = (await res.json()) as ApiResponse<{ dismissed: boolean }>;
  if (!json.ok) throw new Error(json.error.message);
}

export async function listTaxInvoiceReceipts(): Promise<TaxInvoiceReceiptRow[]> {
  const res = await fetch(`${BASE}/sales/tax-invoice-receipts`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<{ items: TaxInvoiceReceiptRow[] }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.items;
}

// ── 세금계산서 수동 지정 (v2.47.0) ────────────────────────────

/** 지정 대상 영업건 후보 1건 */
export interface TaxInvoiceCandidate {
  salesId: string;
  companyId: string;
  status: string;
  invoiceCompany: string | null;
  proposalCompany: string | null;
  endUser: string | null;
  projectName: string | null;
  proposalPrice: number | null;
  invoiceDate: string | null;
  deliveryDate: string | null;
  invoiceRequestAt: string | null;
  /** 이 영업건에 이미 붙어 있는 세금계산서 장수 — 1 이상이면 수정발행 경고 */
  taxInvoiceCount: number;
  /** 근거 (v2.311.0 · #25) — 그 건 발행요청(열린 것 먼저)의 품목명들·[비고] · 계산서 비고란에 그 [비고]가 있나 · 옛 서버는 없음 */
  requestItemNames?: string[];
  requestRemark?: string | null;
  remarkHit?: boolean;
  /** 계산서 품목 글이 이 건의 프로젝트명('project')·요청 품목명('item')·둘 다('both')와 맞나 (v2.311.0 · 2단계) */
  itemHit?: 'project' | 'item' | 'both' | null;
}

export interface TaxInvoiceCandidateResult {
  receipt: {
    id: number;
    approvalNo: string;
    matchStatus: string;
    issueDate: string | null;
    supplyAmount: number | null;
    buyerName: string | null;
    buyerBizNo: string | null;
    filename: string | null;
    /** 이 계산서를 발행한 자사 ('SM'|'EJ'|null) — 다른 사업자 영업건을 고르면 경고 */
    supplierCompanyId: string | null;
    attachmentId: number | null;
    /** 계산서 비고란에서 읽은 글 (v2.311.0) */
    pdfRemark?: string | null;
    /** 계산서 품목 칸 글 (v2.311.0 · 2단계 — 보관본 PDF 글자 층 · 스캔본은 빈 목록) */
    pdfItems?: string[];
  };
  /** 'suggest' = 금액·사업자번호로 추천, 'search' = 사용자 검색어 결과 */
  mode: 'suggest' | 'search';
  candidates: TaxInvoiceCandidate[];
}

export async function listTaxInvoiceCandidates(
  receiptId: number,
  q?: string,
): Promise<TaxInvoiceCandidateResult> {
  const qs = q?.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
  const res = await fetch(`${BASE}/sales/tax-invoice-receipts/${receiptId}/candidates${qs}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<TaxInvoiceCandidateResult>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export interface TaxInvoiceAssignResult {
  applied: boolean;
  /** 재고 음수 경고 — applied=false 면 아무것도 적용되지 않았다(확인 후 force 재전송) */
  warnings: { itemCode: string; itemName: string | null; currentStock: number }[];
  salesId?: string;
  statusBefore?: string;
  statusAfter?: string;
  invoiceDateBefore?: string | null;
  invoiceDateAfter?: string | null;
  attachmentId?: number;
}

export async function assignTaxInvoiceReceipt(
  receiptId: number,
  body: { salesId: string; updateInvoiceDate: boolean; force?: boolean },
): Promise<TaxInvoiceAssignResult> {
  const res = await fetch(`${BASE}/sales/tax-invoice-receipts/${receiptId}/assign`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as ApiResponse<TaxInvoiceAssignResult>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

// ── 🔧 세금계산서 → 유지보수 계약의 한 달분으로 지정 (v2.262.0) ─────────

export interface MaintInvoiceCandidate {
  contractId: number;
  endUser: string;
  contractName: string | null;
  contractCompany: string | null;
  contractNo: string | null;
  companyId: string;
  status: string;
  billingCycle: string | null;
  perInvoiceAmount: number | null;
  contractStart: string | null;
  contractEnd: string | null;
  /** 지금 유효한 계약(확정 + 오늘이 계약 기간 안) — v2.263.0 */
  isValid: boolean;
  /** 청구업체 사업자번호가 계산서 공급받는자와 같다 */
  buyerMatch: boolean;
  /** 1회 청구액 또는 열린 발행요청 금액이 공급가액과 같다(±2원) */
  amountMatch: boolean;
  /** 아직 계산서가 안 온 유지보수 발행요청 */
  openRequests: { year: number; month: number; amount: number; requestedAt: string }[];
  suggestYear: number | null;
  suggestMonth: number | null;
}

export interface MaintInvoiceCandidateResult {
  mode: 'suggest' | 'search';
  /** true = 지금 유효한 계약만 보여 줬다(기본) · hiddenCount = 그래서 숨긴 계약 수 (v2.263.0) */
  validOnly: boolean;
  hiddenCount: number;
  /** 이 계산서를 발행한 자사 ('SM'|'EJ'|null) — 다른 사업자 계약을 고르면 경고 */
  supplierCompanyId: string | null;
  candidates: MaintInvoiceCandidate[];
}

export async function listMaintInvoiceCandidates(
  receiptId: number,
  q?: string,
  /** true = 끝난·미정 계약까지 */
  all?: boolean,
): Promise<MaintInvoiceCandidateResult> {
  const sp = new URLSearchParams();
  if (q?.trim()) sp.set('q', q.trim());
  if (all) sp.set('all', '1');
  const qs = sp.toString() ? `?${sp.toString()}` : '';
  const res = await fetch(`${BASE}/sales/tax-invoice-receipts/${receiptId}/maint-candidates${qs}`, {
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<MaintInvoiceCandidateResult>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export interface MaintInvoiceMonth {
  month: number;
  inContract: boolean;
  rows: { maintenanceId: number; amount: number; invoiceDate: string | null; linkedApprovalNos: string[] }[];
  openRequest: { amount: number; requestedAt: string } | null;
}

export async function listMaintInvoiceMonths(
  receiptId: number,
  contractId: number,
  year: number,
): Promise<{ contractId: number; year: number; months: MaintInvoiceMonth[] }> {
  const res = await fetch(
    `${BASE}/sales/tax-invoice-receipts/${receiptId}/maint-months?contractId=${contractId}&year=${year}`,
    { credentials: 'include' },
  );
  const json = (await res.json()) as ApiResponse<{ contractId: number; year: number; months: MaintInvoiceMonth[] }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export interface MaintInvoiceAssignResult {
  applied: boolean;
  maintenanceId: number;
  /** true = 그 달에 발행이 없어 계산서로 새로 등록했다 */
  created: boolean;
  label: string;
  invoiceDateBefore: string | null;
  invoiceDateAfter: string | null;
  attachmentId: number;
  amountNote: string | null;
}

export async function assignTaxInvoiceToMaintenance(
  receiptId: number,
  body: { contractId: number; year: number; month: number; maintenanceId?: number | null; updateInvoiceDate: boolean },
): Promise<MaintInvoiceAssignResult> {
  const res = await fetch(`${BASE}/sales/tax-invoice-receipts/${receiptId}/assign-maintenance`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as ApiResponse<MaintInvoiceAssignResult>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}
